import { apiDepartments } from "@/api/department.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import {
  ApiDepartmentFilter,
  ApiDepartmentSavePayload,
  buildDepartmentTree,
} from "@/types/department.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";
import { escapeRegExp } from "lodash";

const departmentsKeys = createQueryKeys("departments", {
  allTree: null,
  all: null,
  search: (filter: ApiDepartmentFilter) => ({
    queryKey: [filter],
    queryFn: async ({ signal }) => {
      const result = await apiDepartments.search(filter, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  byListOfId: (ids: number[]) => ({
    queryKey: [ids],
    queryFn: async () => {
      const result = await apiDepartments.getByListOfId(ids);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const useDepartmentsTree = (enabled = true) => {
  return useQuery({
    ...departmentsKeys.allTree,
    queryFn: async () => {
      const result = await apiDepartments.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return buildDepartmentTree(result.data || []);
    },
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};
export const useDepartments = (enabled = true) => {
  return useQuery({
    ...departmentsKeys.all,
    queryFn: async () => {
      const result = await apiDepartments.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 60 * 1000,
    enabled: enabled,
  });
};

export interface DepartmentSaveParams {
  id: number;
  payload: ApiDepartmentSavePayload;
}

export const useDepartmentSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async function ({ id, payload }: DepartmentSaveParams) {
      const result =
        id == 0 ? await apiDepartments.create(payload) : await apiDepartments.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: departmentsKeys.all.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: departmentsKeys.allTree.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useDepartmentDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiDepartments.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: departmentsKeys.all.queryKey,
      });
      queryClient.invalidateQueries({
        queryKey: departmentsKeys.allTree.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useDepartmentsSearchInline = (enabled = true) => {
  const { data, isLoading } = useDepartmentsTree(enabled);
  const [searchText, setSearchText] = useState<string | undefined>(undefined);
  const options = useMemo(() => {
    if (searchText) {
      const regex = new RegExp(escapeRegExp(searchText), "i");
      return data?.filter((option) => regex.test(option.title));
    }
    return data || [];
  }, [data, searchText]);

  function search(text: string | undefined) {
    setSearchText(text);
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};
